import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, History, Hourglass, PiggyBank, Receipt } from "lucide-react";
import { lpApi, lpErr, money, day, card, h2, input, btn, ghost } from "@/components/portal/lp/lpApi";
import { PortalScreen, Tile } from "@/components/portal/lp/PortalScreen";

const BUCKETS = [["checking", "Cash in Hand"], ["savings", "Savings"], ["loan", "Line of Credit"], ["escrow", "Rent"]];

function Balances({ d }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="money-balances">
      {BUCKETS.map(([k, label]) => (
        <div key={k} className={card} data-testid={`money-balance-${k}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
          <div className={`text-xl font-semibold ${d.balances[k] < 0 ? "text-red-300" : "text-white"}`}>{money(d.balances[k])}</div>
        </div>
      ))}
    </div>
  );
}

function Withdraw({ api, savings, onDone }) {
  const [f, setF] = useState({ amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/portal/savings/request-withdrawal", { amount: Number(f.amount), reason: f.reason }); toast.success("Request sent — staff will review it"); setF({ amount: "", reason: "" }); onDone(); }
    catch (err) { toast.error(lpErr(err)); } finally { setBusy(false); }
  };
  return (
    <section className={`${card} space-y-3`} data-testid="money-savings">
      <div className="flex items-baseline justify-between"><h2 className={h2}>Savings</h2><span className="text-sm text-white/60">Available <b data-testid="money-savings-available" className="text-white">{money(savings.available)}</b></span></div>
      <form onSubmit={send} className="grid gap-2 sm:grid-cols-[120px_1fr_auto]">
        <input data-testid="money-withdraw-amount" className={input} type="number" min="1" step="0.01" placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required />
        <input data-testid="money-withdraw-reason" className={input} placeholder="What it's for" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} required />
        <button data-testid="money-withdraw-submit" className={btn} disabled={busy}>Request</button>
      </form>
      <ul className="divide-y divide-white/5 text-sm" data-testid="money-withdraw-history">
        {savings.requests.slice(0, 8).map((x) => (
          <li key={x.id} className="flex justify-between py-2"><span className="truncate">{day(x.created_at)} · {x.reason || x.request_type}</span><span className="text-white/60 shrink-0 ml-2">{money(x.amount)} · <span className={x.status === "pending" ? "text-amber-300" : x.status === "approved" ? "text-emerald-300" : "text-white/50"}>{x.status}</span></span></li>
        ))}
        {!savings.requests.length && <li className="py-2 text-white/40">No withdrawal requests yet.</li>}
      </ul>
    </section>
  );
}

const SPLIT = [["tithe", "Tithe"], ["rent", "Rent"], ["case_management", "PACT Fee"], ["savings", "Savings"], ["checking", "Cash in Hand"]];
const blankPay = () => ({ pay_period_start: "", pay_period_end: "", employer: "", gross_amount: "", split: { tithe: "", rent: "", case_management: "", savings: "", checking: "" } });

const LOCKED = ["rent", "case_management"];
const str = (v) => (v ? String(v) : "");

function LogPaycheck({ api, onDone }) {
  const [f, setF] = useState(blankPay);
  const [busy, setBusy] = useState(false);
  const [pre, setPre] = useState(null);
  useEffect(() => {
    api.get("/portal/resident/financials/paycheck-prefill").then(({ prefill: p }) => {
      if (!p) return;
      setPre(p);
      setF({ pay_period_start: p.pay_period_start, pay_period_end: p.pay_period_end, employer: p.employer, gross_amount: String(p.gross_amount),
        split: { tithe: "", savings: "", rent: str(p.split.rent), case_management: str(p.split.case_management), checking: str(p.split.checking) } });
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const lock = !!pre;
  const placed = Object.values(f.split).reduce((a, v) => a + (Number(v) || 0), 0);
  const left = Math.round(((Number(f.gross_amount) || 0) - placed) * 100) / 100;
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const split = Object.fromEntries(Object.entries(f.split).map(([k, v]) => [k, Number(v) || 0]));
      await api.post("/portal/resident/financials/paychecks", { ...f, gross_amount: Number(f.gross_amount), split, ...(lock ? { line_id: pre.line_id } : {}) });
      toast.success("Paycheck logged — staff will verify it"); setF(blankPay()); onDone();
    } catch (err) { toast.error(lpErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="space-y-3" data-testid="money-paycheck-form">
      {lock && (
        <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-3 text-sm space-y-1" data-testid="money-paycheck-prefill">
          <div className="text-emerald-200">From invoice #{pre.invoice_number} · {pre.vendor_name}</div>
          <div className="text-xs text-white/60">The check amount, Rent and PACT Fee come from the invoice. You can move Cash in Hand to Savings or Tithe.</div>
          <button type="button" className="text-xs text-white/50 underline" data-testid="money-paycheck-not-invoice" onClick={() => { setPre(null); setF(blankPay()); }}>This is a different paycheck</button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-white/50">Period start<input type="date" className={input} required readOnly={lock} value={f.pay_period_start} onChange={(e) => setF({ ...f, pay_period_start: e.target.value })} data-testid="money-paycheck-start" /></label>
        <label className="text-xs text-white/50">Period end<input type="date" className={input} required readOnly={lock} value={f.pay_period_end} onChange={(e) => setF({ ...f, pay_period_end: e.target.value })} data-testid="money-paycheck-end" /></label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input className={input} placeholder="Employer" readOnly={lock} value={f.employer} onChange={(e) => setF({ ...f, employer: e.target.value })} data-testid="money-paycheck-employer" />
        <input className={input} type="number" min="0.01" step="0.01" placeholder="Total take-home $" required readOnly={lock} value={f.gross_amount} onChange={(e) => setF({ ...f, gross_amount: e.target.value })} data-testid="money-paycheck-gross" />
      </div>
      <div className={`${card} space-y-2`}>
        <div className="flex justify-between text-sm"><span className={h2}>Where every dollar goes</span>
          <span data-testid="money-paycheck-left" className={left === 0 ? "text-emerald-300" : "text-amber-300"}>{left === 0 ? "All placed" : left > 0 ? `${money(left)} left to place` : `${money(-left)} over — take some back`}</span></div>
        {SPLIT.map(([k, label]) => (
          <label key={k} className="flex items-center gap-3 text-sm">
            <span className="flex-1">{label}</span>
            <input className={`${input} !w-32 text-right ${lock && LOCKED.includes(k) ? "opacity-60" : ""}`} type="number" min="0" step="0.01" placeholder="0.00" value={f.split[k]}
              readOnly={lock && LOCKED.includes(k)}
              onChange={(e) => setF({ ...f, split: { ...f.split, [k]: e.target.value } })} data-testid={`money-paycheck-split-${k}`} />
          </label>
        ))}
        {left > 0 && <button type="button" className={ghost} data-testid="money-paycheck-rest-checking" onClick={() => setF({ ...f, split: { ...f.split, checking: String(Math.round(((Number(f.split.checking) || 0) + left) * 100) / 100) } })}>Put the rest in Cash in Hand</button>}
      </div>
      <button className={`${btn} w-full`} disabled={busy || !(Number(f.gross_amount) > 0) || left !== 0} data-testid="money-paycheck-submit">{busy ? "Sending…" : "Log paycheck"}</button>
    </form>
  );
}

function PaycheckHistory({ rows }) {
  return (
    <ul className="divide-y divide-white/5 text-sm" data-testid="money-paycheck-history">
      {rows.map((p) => (
        <li key={p.id} className="flex justify-between py-2" data-testid={`money-paycheck-${p.id}`}><span className="truncate">{day(p.pay_period_end)} · {p.employer || "Paycheck"}</span><span className="text-white/60 shrink-0 ml-2">{money(p.gross_amount)} · {String(p.status).replace(/_/g, " ")}</span></li>
      ))}
      {!rows.length && <li className="py-2 text-white/40">No paychecks yet.</li>}
    </ul>
  );
}

function Expenses({ api, d, onDone }) {
  const [f, setF] = useState({ amount: "", category: "food", note: "" });
  const send = async (e) => {
    e.preventDefault();
    try { await api.post("/portal/resident/financials/expenses", { ...f, amount: Number(f.amount) }); toast.success("Expense logged"); setF({ amount: "", category: f.category, note: "" }); onDone(); }
    catch (err) { toast.error(lpErr(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this expense?")) return;
    try { await api.del(`/portal/resident/financials/expenses/${id}`); toast.success("Expense removed"); onDone(); } catch (err) { toast.error(lpErr(err)); }
  };
  return (
    <section className={`${card} space-y-3`} data-testid="money-expenses">
      <div className="flex items-baseline justify-between"><h2 className={h2}>Expenses</h2><span className="text-sm text-white/60">This month <b data-testid="money-expenses-month" className="text-white">{money(d.this_month_total)}</b></span></div>
      <form onSubmit={send} className="grid gap-2 grid-cols-2 sm:grid-cols-[110px_140px_1fr_auto]">
        <input className={input} type="number" min="0.01" step="0.01" placeholder="Amount" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="money-expense-amount" />
        <select className={input} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="money-expense-category">
          {d.categories.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
        </select>
        <input className={`${input} col-span-2 sm:col-span-1`} placeholder="Note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="money-expense-note" />
        <button className={`${btn} col-span-2 sm:col-span-1`} data-testid="money-expense-submit">Add</button>
      </form>
      <ul className="divide-y divide-white/5 text-sm">
        {d.expenses.slice(0, 15).map((x) => (
          <li key={x.id} className="flex items-center justify-between py-2" data-testid={`money-expense-${x.id}`}>
            <span className="truncate">{day(x.spent_on)} · {x.category}{x.note ? ` · ${x.note}` : ""}</span>
            <span className="flex items-center gap-2 shrink-0 ml-2">{money(x.amount)}<button type="button" onClick={() => remove(x.id)} data-testid={`money-expense-delete-${x.id}`} className="text-white/40 hover:text-red-300 text-xs">remove</button></span>
          </li>
        ))}
        {!d.expenses.length && <li className="py-2 text-white/40">Nothing logged yet.</li>}
      </ul>
    </section>
  );
}

function ReviewHold({ message }) {
  return (
    <div data-testid="money-review-hold" className={`${card} flex flex-col items-center text-center gap-3 py-10 px-6`}>
      <span className="h-14 w-14 grid place-items-center rounded-full bg-white/5 ring-1 ring-white/10"><Hourglass className="h-6 w-6 text-white/60" /></span>
      <p data-testid="money-review-message" className="text-white/80 text-base leading-relaxed max-w-xs">{message || "Your balance is being reviewed by your case manager. Check back soon."}</p>
    </div>
  );
}

export default function MoneyTab({ token }) {
  const api = lpApi(token);
  const [s, setS] = useState(null);
  const [screen, setScreen] = useState(null);
  const [review, setReview] = useState(null);
  const load = useCallback(() => api.get("/portal/resident/financial-review").then((rv) => {
    setReview(rv);
    if (rv.on) return null;
    return Promise.all([
      api.get("/portal/resident/financials/v2"), api.get("/portal/resident/savings"), api.get("/portal/resident/financials/paychecks"), api.get("/portal/resident/financials/expenses"),
    ]).then(([o, sv, p, x]) => setS({ o, sv, p: p.paychecks, x }));
  }).catch((e) => (e?.response?.status === 423 ? setReview({ on: true, message: e.response.data?.detail }) : toast.error(lpErr(e)))), [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (review?.on) return <ReviewHold message={review.message} />;
  if (!s) return <p className="text-white/50 text-sm">Loading your money…</p>;
  const close = () => setScreen(null);
  const SCREENS = {
    paycheck: ["Log paycheck", <LogPaycheck api={api} onDone={() => { load(); close(); }} />],
    withdraw: ["Savings & withdrawals", <Withdraw api={api} savings={s.sv} onDone={load} />],
    expense: ["Expenses", <Expenses api={api} d={s.x} onDone={load} />],
    paychecks: ["My paychecks", <PaycheckHistory rows={s.p} />],
  };
  const pending = s.p.filter((p) => p.status === "pending_verification").length;
  return (
    <div className="space-y-4" data-testid="tab-money">
      {review?.staff_view && <p data-testid="money-review-staff-note" className="text-[11px] px-3 py-2 rounded-lg bg-amber-400/15 text-amber-200 border border-amber-300/30">Staff view · Financial Review is on, so the resident sees a holding message here.</p>}
      <Balances d={s.o} />
      <div className="grid grid-cols-2 gap-2">
        <Tile icon={Banknote} label="Log paycheck" sub="Put every dollar where it goes" accent onClick={() => setScreen("paycheck")} testid="money-tile-paycheck" />
        <Tile icon={PiggyBank} label="Savings" sub={`${money(s.sv.available)} available`} onClick={() => setScreen("withdraw")} testid="money-tile-withdraw" />
        <Tile icon={Receipt} label="Expenses" sub={`${money(s.x.this_month_total)} this month`} onClick={() => setScreen("expense")} testid="money-tile-expense" />
        <Tile icon={History} label="My paychecks" sub={pending ? `${pending} waiting for staff` : `${s.p.length} logged`} onClick={() => setScreen("paychecks")} testid="money-tile-paychecks" />
      </div>
      {screen && <PortalScreen title={SCREENS[screen][0]} onBack={close} testid={`money-screen-${screen}`}>{SCREENS[screen][1]}</PortalScreen>}
    </div>
  );
}
